<template>
  <div class="similar-strains" v-if="allStrains.length > 0">
    <h2 class="divider-sec">
      Compare with {{ currentStrain.name }}
    </h2>

    <!-- <div class="comparison-description">
      
    </div> -->

    <div class="comparison-table">
      <!-- Strains header with images -->
      <div class="strains-header">
        <div class="empty-column"></div>
        <div class="strain-column" v-for="strain in displayStrains" :key="strain.id">
          <NuxtLink :to="strain.link" class="strain-card">
            <img loading="lazy" 
              :src="strain.cover_s || '/images/strain-cover-empty.svg'" 
              :alt="strain.name"
              class="strain-image"
            />
            <h3 class="strain-name">{{ strain.name }}</h3>
          </NuxtLink>
        </div>
      </div>

      <!-- Comparison rows -->
      <div class="comparison-rows">
        <!-- Type row -->
        <div class="comparison-row">
          <div class="row-label">Type</div>
          <div class="row-values">
            <div 
              class="row-value" 
              v-for="strain in displayStrains" 
              :key="`type-${strain.id}`"
            >
              {{ getGeneType(strain) }}
            </div>
          </div>
        </div>

        <!-- Cannabinoid row -->
        <div class="comparison-row">
          <div class="row-label">Cannabinoid</div>
          <div class="row-values">
            <div 
              class="row-value" 
              v-for="strain in displayStrains" 
              :key="`thc-${strain.id}`"
            >
              {{ getThcValue(strain) }}
            </div>
          </div>
        </div>

        <!-- Top Effect row -->
        <div class="comparison-row">
          <div class="row-label">Top Effect</div>
          <div class="row-values">
            <div 
              class="row-value" 
              v-for="strain in displayStrains" 
              :key="`effect-${strain.id}`"
            >
              {{ getTopEffect(strain) }}
            </div>
          </div>
        </div>

        <!-- Dominant terpene row -->
        <div class="comparison-row">
          <div class="row-label">Dominant terpene</div>
          <div class="row-values">
            <div 
              class="row-value" 
              v-for="strain in displayStrains" 
              :key="`terpene-${strain.id}`"
            >
              {{ getDominantTerpene(strain) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Strain } from '@/types/strain'

interface Props {
  currentStrain: Strain;
  similarStrains: Strain[];
  maxDisplay?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxDisplay: 10
})

const allStrains = computed(() => {
  return [props.currentStrain, ...props.similarStrains]
})

const displayStrains = computed(() => {
  return allStrains.value.slice(0, props.maxDisplay)
})

const getGeneType = (strain: Strain) => {
  const genes = strain.props?.seed_strain_genes
  if (!genes) return '-'
  
  return genes.charAt(0).toUpperCase() + genes.slice(1)
}

const getThcValue = (strain: Strain) => {
  const thc = strain.props?.seed_strain_thc
  if (!thc) return '-'
  
  return `THC ${thc}%`
}

const getTopEffect = (strain: Strain) => {
  const effects = strain.props?.seed_strain_effects_positive
  if (!effects) return '-'
  
  const effectsList = effects.split(',')
  const topEffect = effectsList[0]?.trim()
  
  if (!topEffect) return '-'
  
  return topEffect.charAt(0).toUpperCase() + topEffect.slice(1)
}

const getDominantTerpene = (strain: Strain) => {
  // For now, we'll use a placeholder since terpene data isn't in the provided structure
  // You can add terpene field to strain props if available
  const tastes = strain.props?.seed_strain_tastes
  if (!tastes) return 'Caryophyllene'
  
  const tastesList = tastes.split(',')
  const dominantTaste = tastesList[0]?.trim()
  
  // Map tastes to common terpenes (simplified mapping)
  const tasteToTerpene: Record<string, string> = {
    'citrus': 'Limonene',
    'lemon': 'Limonene', 
    'pine': 'Pinene',
    'flowery': 'Linalool',
    'sweet': 'Myrcene',
    'earthy': 'Myrcene',
    'spicy': 'Caryophyllene',
    'pepper': 'Caryophyllene',
    'diesel': 'Caryophyllene'
  }
  
  return tasteToTerpene[dominantTaste?.toLowerCase()] || 'Caryophyllene'
}
</script>

<style scoped>



.similar-strains {
  margin-bottom: 2rem;
}

.comparison-description {
  color: var(--un-text-color-gray);
  margin-bottom: 2rem;
  font-size: 0.95rem;
  line-height: 1.5;
}

.comparison-table {
  background-color: var(--un-background-color);
  border-radius: 15px;
  /* padding: 2rem; */
  overflow-x: auto;
}

/* Strains header */
.strains-header {
  display: flex;
  flex-direction: row;
  margin-bottom: 1rem;

}

.empty-column {
  width: 170px;
  flex-shrink: 0;
}

.strain-column {
  display: flex;
  justify-content: center;
  width: 170px;
  flex-shrink: 0;
}

.strain-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: var(--color-text);
  transition: opacity 0.2s;
}

.strain-card:hover {
  opacity: 0.8;
}

.strain-image {
  width: 70%;
  height: auto;
  aspect-ratio: 77/120;
  object-fit: cover;
  border-radius: 8px;
}

.strain-name {
  font-weight: bold;
  text-align: center;
  margin: 0;
}

/* Comparison rows */
.comparison-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.comparison-row:nth-child(odd) {
  background-color: var(--un-background-color-gray);
}

.comparison-row {
  display: flex;
  flex-direction: row;
  gap: 0;
  min-height: 50px;
  width: fit-content;
}

.comparison-row:last-child {
  border-bottom: none;
}

.row-label {
  display: flex;
  width: 170px;
  align-items: center;
  padding: 1rem;
  font-weight: bold;
  flex-shrink: 0;
}

.row-values {
  display: flex;
  flex-direction: row;
}

.row-value {
  width: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  font-weight: 500;
  flex-shrink: 0;
}

.row-value:last-child {
  border-right: none;
}

/* Mobile responsive */
@container pb (max-width: 768px) {
  
}

</style>
